import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { HomeGuard } from 'app/core/auth/guards/home.guard';
import {AssociationPermissionsGuard} from 'app/core/auth/guards/association-permissions.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { NotFoundComponent } from '@portal/shared/not-found/not-found.component';
import {InitialDataResolver} from "./app.resolvers";
import { AppContextOrgTypeGuard } from 'app/core/auth/guards/app-context-org-type.guard';
import { AppContextPartiesGuard } from 'app/core/auth/guards/app-context-parties.guard';

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Public homepage: guests see the marketing homepage, authenticated users
    // are sent straight to '/parties' by the HomeGuard.
    {
        path: '',
        canActivate: [HomeGuard],
        canActivateChild: [HomeGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: '', loadChildren: () => import('app/modules/landing/home/home.routing').then(m => m.landingHomeRoutes) },
        ]
    },

    // Redirect signed in user to the '/example'
    //
    // After the user signs in, the sign in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'parties' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routing').then(m => m.authConfirmationRequiredRoutes)
            },
            {
                path: 'confirm-email',
                loadChildren: () => import('app/modules/auth/confirm-email/confirm-email.routing').then(m => m.authConfirmationEmailRoutes)
            },
            {
                path: 'forgot-password',
                loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routing').then(m => m.authForgotPasswordRoutes)
            },
            {
                path: 'reset-password',
                loadChildren: () => import('app/modules/auth/reset-password/reset-password.routing').then(m => m.authResetPasswordRoutes)
            },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routing').then(m => m.authSignInRoutes) },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routing').then(m => m.authSignupRoutes) },
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routing').then(m => m.authSignOutRoutes) },
            {
                path: 'unlock-session',
                loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routing').then(m => m.authUnlockSessionRoutes)
            }
        ]
    },

    // Landing routes
    {

        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: '', loadChildren: () => import('app/modules/landing/landing.routes').then(m => m.landingRoutes) },
        ]
    },

    // Public routes (no authentication required)
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'public', loadChildren: () => import('app/modules/public/public.routes').then(m => m.publicRoutes) },
        ]
    },

    // Admin routes
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        children: [
            {
                path: 'parties',
                canActivate: [AppContextPartiesGuard],
                loadChildren: () => import('app/modules/admin/user-parties/user-parties.routes').then(m => m.userPartiesRoutes)
            },
            {
                path: 'party',
                canActivate: [AppContextOrgTypeGuard],
                canActivateChild: [AppContextOrgTypeGuard, AssociationPermissionsGuard],
                loadChildren: () => import('app/modules/admin/party/party.routes').then(m => m.partyRoutes)
            },
            {
                path: 'org/:orgId/merchandising',
                pathMatch: 'full',
                redirectTo: 'org/:orgId/merchandising/overview'
            },
            {
                path: 'org/:orgId/inventory',
                canActivate: [AppContextOrgTypeGuard],
                canActivateChild: [AppContextOrgTypeGuard, AssociationPermissionsGuard],
                loadChildren: () => import('app/modules/admin/party/inventory/inventory.routes').then(m => m.routes)
            },
            {
                path: 'org/:orgId/merchandising',
                canActivate: [AppContextOrgTypeGuard],
                canActivateChild: [AppContextOrgTypeGuard, AssociationPermissionsGuard],
                loadChildren: () => import('app/modules/admin/party/inventory/inventory.routes').then(m => m.routes)
            },
            {
                path: 'org/:orgId/party-events/:partyEventId/inventory',
                redirectTo: 'party/events/:partyEventId/inventory'
            },
            {
                path: 'organizations',
                canActivate: [AppContextOrgTypeGuard],
                canActivateChild: [AppContextOrgTypeGuard, AssociationPermissionsGuard],
                loadChildren: () => import('app/modules/admin/organizations/organizations.routes').then(m => m.routes)
            },
            {
                path: 'payments',
                loadChildren: () => import('app/modules/admin/payments/payments.routes').then(m => m.paymentsRoutes),
                resolve: {
                    initialData: InitialDataResolver
                },
            },
            {
                path: 'catalog',
                loadComponent: () => import('./modules/admin/party/product-catalog/product-catalog.component').then(m => m.ProductCatalogComponent),
                data: {
                    authRedirect: '/product-catalog'
                }
            },
            {
                path: 'catalog/quote-request',
                loadComponent: () => import('./modules/admin/party/product-catalog/quote-request/quote-request.component').then(m => m.QuoteRequestComponent),
                data: {
                    authRedirect: '/product-catalog/quote'
                }
            },
            {
                path: 'support', loadChildren: () => import('app/modules/admin/support/support.routes').then(m => m.supportRoutes),
                resolve: {
                    initialData: InitialDataResolver
                },
            },
            {
                path: 'admin',
                loadChildren: () => import('app/modules/management/management.routes').then(m => m.managementRoutes)
            },
            {
                path: 'admin/project-tasks',
                loadChildren: () => import('app/modules/admin/project-tasks/project-tasks.routes').then(m => m.routes)
            },
            {
                path: 'timeline',
                loadChildren: () => import('app/modules/public/public.routes').then(m => m.publicRoutes)
            },
        ]
    },
    { path: '**', component: NotFoundComponent },
];
